import { useMutation, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { Alert, Badge, Breadcrumb, BreadcrumbItem, Button, Col, Row, Table, Tooltip } from "reactstrap";
import Loading from "../../components/Loading";
import PromptDialog from "../../components/PromptDialog";
import { getEmpowerMeTaskSchedule } from "../../graphql/empower-me/EmpowerMeQueries";
import { SoftDeleteScheduledEmpowerMeTask, SoftDeleteScheduledEmpowerMeTaskBundle } from "../../graphql/empower-me/mutations/SoftDeleteEmpowerMeTask";
import { EmpowerScheduledTask } from "../../interfaces/models/EmpowerTask";
import EmpowerMeTaskModal from "./EmpowerMeTaskModal";
import EmpowerMeTaskTable from "./EmpowerMeTaskTable";
import toast from "react-hot-toast";
import { IsEmpowerNudgeActive } from "../../helper/empowerNudgeHelpers";
import "./EmpowerMePage.scss";
import { DateFormat, UserActivityTypeWithGroup } from "../../helper/constants"
import { AddActivityHookExecute, ModelEnums } from "../../helper/addUserActivity"
import { EmpowerMeBundle } from "../../interfaces/models/EmpowerMeBundle";
import React from "react";
import { getImageSmallUrl } from "../../helper/getImageUrl";
import moment from "moment";
import { parseRRule } from "../../helper/rruleUtilities";
import { useHistory } from "react-router-dom";
import { DASHBOARD_ROOT, EMPOWER_ME } from "../../components/Routes/routingConstants";
import styles from "../../assets/css/EmpowerMeScheduler.module.css"

const MAX_SCHEDULED_EMPOWER_ME_TASKS = 10;
const MAX_BUNDLE = 2;

export default function EmpowerMePage() {

  const queryParams = new URLSearchParams(window.location.search);
  const isSingle = queryParams.get("isSingle");
  const history = useHistory();

  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [isDeletePromptOpen, setIsDeletePromptOpen] = useState<boolean>(false);
  const [showBundle, setShowBundle] = useState<boolean>(false);
  
  const { executeAddActivity } = AddActivityHookExecute();

  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
  const toggleDisabledCreateTooltip = () => {
    setTooltipOpen(!tooltipOpen);
  };

  const [selectedTask, setSelectedEmpowerTask] = useState<EmpowerScheduledTask>(null);
  const [selectedBundle, setSelectedBundle] =useState<EmpowerMeBundle>(null);
  const [empowerTasks, setEmpowerTasks] = useState<EmpowerScheduledTask[]>([]);
  const [empowerTasksBundles, setEmpowerTasksBundles] = useState<EmpowerMeBundle[]>([]);
  const {
    data: usersEmpowerMeTasks,
    loading,
    error,
    refetch,
  } = useQuery(getEmpowerMeTaskSchedule, {
    onCompleted(data) {
      const results =
        data?.me?.empower_task_schedule?.scheduled_tasks?.map((task) => {
          return task;
        }) ?? [];
      setEmpowerTasks(results);
      setEmpowerTasksBundles(data?.me?.empower_task_schedule?.bundle_details ?? []);
    },
    onError(error) {
      console.log(error);
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true, // Useful for invoking `onCompleted` during `refetch` calls
  });

  const [softDeleteScheduledEmpowerMeTask] = useMutation(
    SoftDeleteScheduledEmpowerMeTask
  );
  const [softDeleteScheduledEmpowerMeTaskBundle] = useMutation(
    SoftDeleteScheduledEmpowerMeTaskBundle
  );

  const HandleSync = (query : string) => {
    setTimeout(() => {
        window.location.href = `${DASHBOARD_ROOT}/${EMPOWER_ME}?syncEmpowerMe=true${query}`;
    }, 1200)
  }
  function filterActiveEmpowerMeTasks(empowerMeTasks: EmpowerScheduledTask[]) {
    const active_tasks = empowerMeTasks.filter((task: EmpowerScheduledTask) => {
      return IsEmpowerNudgeActive(task);
    });
    return active_tasks;
  }

  const shouldDisableCreateButton =
    filterActiveEmpowerMeTasks(empowerTasks).length >=
    MAX_SCHEDULED_EMPOWER_ME_TASKS && empowerTasksBundles.length >= MAX_BUNDLE;

  const toggleModal = () => setModalOpen(!isModalOpen);

  function showDeletePrompt(empowerTask: EmpowerScheduledTask, bundle : EmpowerMeBundle) {
    setSelectedEmpowerTask(empowerTask);
    setSelectedBundle(bundle);
    setIsDeletePromptOpen(true);
  }
  const closeDeletePrompt = () => {
    setIsDeletePromptOpen(false);
  };
  function deleteEmpowerMeTask() {
    closeDeletePrompt();
    if(selectedBundle) {
      toast.promise(
        softDeleteScheduledEmpowerMeTaskBundle({
          variables: {
            bundle_id: selectedBundle._id,
          },
        }),
        {
          loading: "Deleting...",
          success: () => {
            // refetch();
            executeAddActivity({
              type : UserActivityTypeWithGroup.EM_DELETE.id, 
              type_group : UserActivityTypeWithGroup.EM_DELETE.group,
              on_model : ModelEnums.EM_BUNDLE,
              related_id : selectedBundle._id,
            });
            HandleSync("&isSingle=false");
            return "Successfully deleted! Please wait we are syncing your EM nudges";
          },
          error:
            "Sorry, your empower me task could not be deleted. Please try again in a few minutes or contact a System Administrator.",
        },
        {
          success: {
            duration: 9000,
            style: {
              textAlign: "center",
            },
          },
        }
      );
      setSelectedEmpowerTask(null);
    }
    else {
      toast.promise(
        softDeleteScheduledEmpowerMeTask({
          variables: {
            id: selectedTask._id,
          },
        }),
        {
          loading: "Deleting...",
          success: () => {
            executeAddActivity({
              type : UserActivityTypeWithGroup.EM_DELETE.id, 
              type_group : UserActivityTypeWithGroup.EM_DELETE.group,
              on_model : ModelEnums.EM,
              related_id : selectedTask.empower_me_task._id,
            });
            HandleSync("&isSingle=true");
            return "Successfully deleted! Please wait we are syncing your EM nudges";
          },
          error:
            "Sorry, your empower me task could not be deleted. Please try again in a few minutes or contact a System Administrator.",
        },
        {
          success: {
            duration: 9000,
            style: {
              textAlign: "center",
            },
          },
        }
      );
      setSelectedEmpowerTask(null);
    }
    
  }

  useEffect(() => {
    if(isSingle) {
      setShowBundle(isSingle === "false");
    }

  },[isSingle]);

  if (loading) return <Loading />;
  // Display error message if user's empower me tasks cannot be retrieved
  if (!usersEmpowerMeTasks || error) {
    return (
      <Alert color="danger">
        Sorry! Something went wrong while retrieving the user's empower me tasks
        - Please try again later or contact a System Administrator.
      </Alert>
    );
  }

  return (
    <>
      <div className="w-100">
        <Breadcrumb listTag="div">
          <BreadcrumbItem active={true}>Home</BreadcrumbItem>
        </Breadcrumb>
      </div>
      <div className="w-100 d-flex flex-row flex-wrap justify-content-between align-items-center">
        <div className="d-flex flex-column">
          <h3>Your empower me Tasks</h3>
        </div>
        <div className="d-flex">
          {/* <Button
            color="secondary"
            onClick={(e) => {
              const baseUrl =
                window.location.protocol +
                "//" +
                window.location.host +
                window.location.pathname;
              window.location.href = baseUrl + "?syncEmpowerMe=true";
            }}
          >
            Sync Tasks
          </Button> */}
          <Button
            color="primary"
            onClick={(e) => {
              // setSelectedEmpowerTask(null);
              // toggleModal();
              history.push(`${DASHBOARD_ROOT}/${EMPOWER_ME}/schedule`)
            }}
            disabled={shouldDisableCreateButton}
          >
            {/* This wrapping div is a workaround in order to have the Tooltip display for a disabled button */}
            <div id="tooltipTarget">Add Task</div>
          </Button>
          <Tooltip
            placement="top"
            isOpen={tooltipOpen && shouldDisableCreateButton}
            target="tooltipTarget"
            toggle={toggleDisabledCreateTooltip}
          >
            You are limited to {MAX_SCHEDULED_EMPOWER_ME_TASKS} active empower
            me tasks. Before scheduling a new task you must delete an existing
            one.
          </Tooltip>
        </div>
      </div>
      
      <Row>
        <Col md={4} className="offset-md-8">
            <div className="d-flex justify-content-end align-items-center pt-3">
              <div className={styles.toggleSwitch}>
                <button
                    className={`${styles.option} ${showBundle === false ? styles.active : ''}`}
                    onClick={() => setShowBundle(false)}
                >
                    Single
                </button>
                <button
                    className={`${styles.option} ${showBundle === true ? styles.active : ''}`}
                    onClick={() => setShowBundle(true)}
                >
                    Bundle
                </button>
              </div>
            </div>
        </Col>
      </Row>
      {
        showBundle ?
        <div className="table-responsive pt-2">
          <Table bordered hover className="bg-white">
            <thead>
              <tr>
                  <th></th>
                  <th>CATEGORY</th>
                  <th>Active/INACTIVE</th>
                  <th>START DATE</th>
                  <th>SCHEDULE</th>
                  <th></th>
              </tr>
            </thead>
            <tbody>
              {
                empowerTasksBundles.length === 0 &&
                <tr>
                  <td colSpan={6} className="text-center">No Empower Me Bundles</td>
                </tr>
              }
              {
                loading ?
                <tr>
                  <td colSpan={6}>
                    <Loading />
                  </td>
                </tr>
                :
                empowerTasksBundles.map((bundle, index) => (

                  <React.Fragment key={index}>
                    <tr className="font-weight-bold bg-lighter">
                      <td colSpan={5} className="text-center">
                        <h2  className="font-weight-bold mb-0">Bundle : {bundle.name}</h2>
                      </td>
                      <td >
                        <button
                          className="table-button"
                          onClick={() => {
                            showDeletePrompt(null, bundle);
                          }}
                        >
                          <i className="fas fa-trash-alt fa-2x" />
                        </button>
                      </td>
                    </tr>

                    {
                      empowerTasks.filter( u => u.bundle_id === bundle._id).map((row, index) => {

                        const isActive = IsEmpowerNudgeActive(row);
                        return (
                          <tr key={index} className="empower-me-task-table-row">
                            <td>
                              <div className="d-flex justify-content-center items-center">
                                <img
                                  alt="thumbnail"
                                  style={{ width: "6rem" }}
                                  src={getImageSmallUrl(row.empower_me_task?.image_id ?? row.empower_me_task?.task_category?.image)}
                                />
                              </div>
                            </td>
                            <td>
                              {
                                row?.empower_me_task?.task_category?.description ? (
                                  <p>{row.empower_me_task.task_category.description}</p>
                                ) : (
                                  <p>No category</p>
                                )
                              }
                            </td>
                            <td>
                              <div className={"d-flex justify-content-center"}>
                                <Badge
                                  style={{ fontWeight: "bold" }}
                                  color={isActive ? "primary" : "danger"}
                                  pill
                                >
                                  {isActive ? "Active" : "Inactive"}
                                </Badge>
                              </div>
                            </td>
                            <td>
                              <p>{moment(row.start_date).format(DateFormat + " h:mm a")}</p>
                            </td>
                            <td>
                              {
                                !row?.recurrence_rule ? 
                                <p>No repeat</p>
                                  :
                                <p>{parseRRule(row.recurrence_rule)}</p>
                              }
                            </td>
                            <td></td>
                          </tr>
                        );
                      })
                    }
                  </React.Fragment>

                ))
              }
            </tbody>
          </Table>
        </div>
        :
        <EmpowerMeTaskTable
          empowerTasks={empowerTasks.filter(u => u.bundle_id === null || u.bundle_id === undefined)}
          deleteEmpowerMeTask={showDeletePrompt}
        />
      }
      {isModalOpen && (
        <EmpowerMeTaskModal
          isOpen={isModalOpen}
          toggle={toggleModal}
          refetchTasks={refetch}
          empowerTasks={empowerTasks}
        />
      )}
      {isDeletePromptOpen && (
        <PromptDialog
          isOpen={isDeletePromptOpen}
          onCancel={closeDeletePrompt}
          onConfirm={deleteEmpowerMeTask}
          title="Delete empower me task?"
          promptMessage="Are you sure you want to delete this empower me task and all scheduled nudges?"
          confirmBtnTxt="Delete"
        />
      )}
    </>
  );
}
